import React, { useState, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import {
    Box, Card, TextField, MenuItem, Select, OutlinedInput, Checkbox, ListItemText, Chip, FormControl, InputLabel,
    Button, Grid, LinearProgress, Alert, Snackbar, Stack, Typography, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem
} from "@mui/material";
import {
    PlayCircleOutline as RunIcon,
    SaveOutlined as SaveIcon,
    UploadFile as UploadIcon,
    FileDownload as DownloadIcon,
    VisibilityOutlined as ViewIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';
import { useNavigate } from 'react-router-dom';
import EffectiveDatedPayrollBreakdown from '../../components/payroll/EffectiveDatedPayrollBreakdown';

const PayrollProcessing = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const gridRef = useRef();
    const [loading, setLoading] = useState(false);
    const [rowData, setRowData] = useState([]);
    const [filters, setFilters] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        run_type: 'REGULAR',
        period_type: 'REGULAR',
        payroll_group_id: '',
        advance_schedule_id: '',
        off_cycle_elements: []
    });
    const [viewFilters, setViewFilters] = useState({ searchText: '', orgNodeId: '', branchId: '', gradeId: '' });
    const [metadata, setMetadata] = useState({ nodes: [], branches: [], grades: [], payrollGroups: [] });
    const [elements, setElements] = React.useState([]);
    const [existingRun, setExistingRun] = useState(null);
    const [readiness, setReadiness] = useState(null);
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });
    const [detailsDialog, setDetailsDialog] = useState({ open: false, title: '', details: [] });
    const [breakdownPayroll, setBreakdownPayroll] = useState(null);

    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' }, { value: 4, label: 'April' },
        { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' },
        { value: 9, label: 'September' }, { value: 10, label: 'October' },
        { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];

    const years = [2025, 2026, 2027];

    React.useEffect(() => {
        const fetchElementsAndMetadata = async () => {
            try {
                const token = localStorage.getItem('token');
                const tenant = localStorage.getItem('tenant') || '';
                const headers = { Authorization: `Bearer ${token}`, 'X-Tenant': tenant };

                const [elementsRes, nodesRes, branchesRes, gradesRes, groupsRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/elements`, { headers, withCredentials: true }),
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, { headers, withCredentials: true }),
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, { headers, withCredentials: true }),
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/grades`, { headers, withCredentials: true }),
                    axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll-groups`, { headers, withCredentials: true })
                ]);

                if (elementsRes.data.success) {
                    setElements(elementsRes.data.data.filter(e => e.processing_type === 'OFF_CYCLE' || e.processing_type === 'BOTH'));
                }
                setMetadata({
                    nodes: nodesRes.data?.data || [],
                    branches: branchesRes.data?.data || [],
                    grades: gradesRes.data?.data || [],
                    payrollGroups: (groupsRes.data?.data || []).filter(group => Number(group.is_active) === 1)
                });
                const activeGroups = (groupsRes.data?.data || []).filter(group => Number(group.is_active) === 1);
                const defaultGroup = activeGroups.find(group => group.group_code === 'DEFAULT') || activeGroups[0];
                if (defaultGroup) setFilters(prev => prev.payroll_group_id ? prev : { ...prev, payroll_group_id: defaultGroup.id });
            } catch (e) { console.error(e); }
        };
        fetchElementsAndMetadata();
    }, []);

    const filteredRowData = useMemo(() => {
        return rowData.filter(row => {
            if (viewFilters.searchText) {
                const search = viewFilters.searchText.toLowerCase();
                const matchesText = (row.full_name || '').toLowerCase().includes(search) ||
                    (row.employee_number || '').toLowerCase().includes(search) ||
                    (row.staff_code || '').toLowerCase().includes(search);
                if (!matchesText) return false;
            }
            if (viewFilters.orgNodeId && row.org_node_id !== viewFilters.orgNodeId) return false;
            if (viewFilters.branchId && row.branch_id !== viewFilters.branchId) return false;
            if (viewFilters.gradeId && row.grade_id !== viewFilters.gradeId) return false;
            return true;
        });
    }, [rowData, viewFilters]);


    const fetchPreview = useCallback(async () => {
        setLoading(true);
        setExistingRun(null);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const params = { ...filters };
            if (filters.run_type === 'OFF_CYCLE') {
                params.off_cycle_elements = JSON.stringify(filters.off_cycle_elements);
            }
            const readinessResponse = filters.run_type === 'REGULAR' ? await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/readiness`, {
                params: { month: filters.month, year: filters.year, payroll_group_id: filters.payroll_group_id, period_type: filters.period_type, advance_schedule_id: filters.advance_schedule_id || undefined },
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant }, withCredentials: true
            }) : null;
            setReadiness(readinessResponse?.data?.data || null);
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/preview`, {
                params: params,
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setRowData(response.data.data);
                setExistingRun(filters.run_type === 'REGULAR' ? (response.data.existingRun || null) : null);
            }
        } catch (error) {
            console.error("Error fetching preview:", error);
            setStatusMsg({ open: true, msg: 'Error calculating preview', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const changePeriodLock = async (lock) => {
        const tenant = localStorage.getItem('tenant') || '';
        const token = localStorage.getItem('token');
        let reason;
        if (!lock) {
            reason = window.prompt('Reason for reopening this payroll period:') || '';
            if (!reason.trim()) return;
        }
        setLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/period/${lock ? 'lock' : 'reopen'}`, { ...filters, reason }, { headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant }, withCredentials: true });
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/readiness`, { params: filters, headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant }, withCredentials: true });
            setReadiness(response.data.data);
            setStatusMsg({ open: true, msg: lock ? 'Payroll period locked' : 'Payroll period reopened', severity: 'success' });
        } catch (error) {
            setStatusMsg({ open: true, msg: error.response?.data?.message || 'Could not update payroll period', severity: 'error' });
        } finally { setLoading(false); }
    };

    const createRun = async () => {
        if (filters.run_type === 'REGULAR' && existingRun) return;
        if (!window.confirm("This will save a DRAFT payroll run for this month. Continue?")) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const payload = {
                ...filters,
                payroll_group_id: filters.payroll_group_id || null,
                advance_schedule_id: filters.advance_schedule_id || null,
            };
            const response = await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/runs`, payload, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setStatusMsg({ open: true, msg: 'Payroll run saved as DRAFT', severity: 'success' });
                if (filters.run_type === 'REGULAR') {
                    setExistingRun({ id: response.data.runId, status: 'DRAFT', period_month: filters.month, period_year: filters.year });
                }
            }
        } catch (error) {
            console.error("Error saving run:", error);
            setStatusMsg({ open: true, msg: 'Error saving payroll run', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const onBtnExport = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.exportDataAsCsv({
                fileName: `payroll_preview_${filters.year}_${filters.month}_${filters.run_type}.csv`
            });
        }
    }, [filters]);



    const columnDefs = useMemo(() => {
        const dynamicAlwCols = new Set();
        const dynamicDedCols = new Set();

        (rowData || []).forEach(row => {
            Object.keys(row).forEach(k => {
                if (k.startsWith('alw_')) dynamicAlwCols.add(k);
                if (k.startsWith('ded_')) dynamicDedCols.add(k);
            });
        });

        const dynamicAdditions = Array.from(dynamicAlwCols).map(k => ({
            field: k,
            headerName: k.replace('alw_', ''),
            width: 120,
            valueFormatter: p => p.value?.toLocaleString(),
            cellStyle: { color: '#2e7d32' }
        }));

        const dynamicDeductions = Array.from(dynamicDedCols).map(k => {
            const raw = k.replace('ded_', '');
            const headerName = raw === 'MISSED_CLOCK_PENALTY' ? 'Missed Clock Fine' : raw;
            return {
                field: k,
                headerName,
                width: 140,
                valueFormatter: p => p.value?.toLocaleString(),
                cellStyle: { color: '#d32f2f' }
            };
        });

        return [
            {
                headerName: "Audit",
                pinned: 'left',
                width: 92,
                sortable: false,
                filter: false,
                cellRenderer: p => (
                    <Button size="small" startIcon={<ViewIcon />} onClick={() => setBreakdownPayroll(p.data)}>
                        View
                    </Button>
                )
            },
            {
                headerName: "Staff",
                pinned: 'left',
                children: [
                    {
                        field: "employee_number",
                        headerName: "Employee Code",
                        width: 140,
                        valueGetter: p => p.data?.employee_number || p.data?.staff_code || '-'
                    },
                    { field: "full_name", headerName: "Name", width: 180 },
                ]
            },
            {
                headerName: "Base Compensation",
                children: [
                    { field: "basic_salary", headerName: "Basic", width: 120, valueFormatter: p => p.value?.toLocaleString() },
                    { field: "hourly_rate", headerName: "H. Rate", width: 100, valueFormatter: p => p.value?.toLocaleString() },
                ]
            },
            {
                headerName: "Additions (OT & Alw)",
                children: [
                    { field: "payable_ot_mins", headerName: "Total OT Min", width: 110 },
                    { field: "ot_pay", headerName: "Total OT Pay", width: 120, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32', fontWeight: 'bold' } },
                    { field: "normal_ot_mins", headerName: "Normal OT Min", width: 120 },
                    { field: "normal_ot_pay", headerName: "Normal OT Pay", width: 120, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    { field: "rest_ot_mins", headerName: "Rest OT Min", width: 110 },
                    { field: "rest_ot_pay", headerName: "Rest OT Pay", width: 110, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    { field: "holiday_ot_mins", headerName: "Holiday OT Min", width: 120 },
                    { field: "holiday_ot_pay", headerName: "Holiday OT Pay", width: 120, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    { field: "extended_ot_mins", headerName: "Extended OT Min", width: 130 },
                    { field: "extended_ot_pay", headerName: "Extended OT Pay", width: 130, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    { field: "meal_allowance", headerName: "Meal Alw.", width: 100, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    { field: "transport_allowance", headerName: "Trans Alw.", width: 110, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    { field: "special_allowance", headerName: "Spec Alw.", width: 100, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#059669' } },
                    { field: "ot_allowance", headerName: "Shift OT Alw.", width: 120, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    ...dynamicAdditions
                ]
            },
            {
                headerName: "Deductions",
                children: [
                    { field: "late_early_penalties", headerName: "Late/Early", width: 110, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#d32f2f' } },
                    { field: "ssb_deduction", headerName: "SSB", width: 110, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#d32f2f' } },
                    { field: "tax_deduction", headerName: "PIT (Tax)", width: 110, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#d32f2f' } },
                    { field: "unpaid_leave_deduction", headerName: "Unpaid Leave", width: 120, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#d32f2f' } },
                    { field: "unpaid_absence_deduction", headerName: "Unpaid Absence", width: 135, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#d32f2f' } },
                    ...dynamicDeductions
                ]
            },
            {
                headerName: "Result",
                children: [
                    { field: "gross_pay", headerName: "Gross Pay", width: 130, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { fontWeight: 'bold' } },
                    { field: "net_pay", headerName: "Net Pay", width: 130, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { fontWeight: 'bold', color: '#1a237e' } },
                ]
            }
        ];
    }, [rowData]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f4f7fe' }}>
            <AppHeader title="Payroll Execution" setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Card sx={{ p: 2, borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={2}>
                            <TextField
                                select fullWidth label="Month" size="small"
                                value={filters.month}
                                onChange={(e) => { setFilters({ ...filters, month: e.target.value }); setExistingRun(null); setReadiness(null); setRowData([]); }}
                            >
                                {months.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                select fullWidth label="Year" size="small"
                                value={filters.year}
                                onChange={(e) => { setFilters({ ...filters, year: e.target.value }); setExistingRun(null); setReadiness(null); setRowData([]); }}
                            >
                                {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                select fullWidth label="Run Type" size="small"
                                value={filters.run_type}
                                onChange={(e) => { setFilters({ ...filters, run_type: e.target.value, off_cycle_elements: [] }); setExistingRun(null); setReadiness(null); setRowData([]); }}
                            >
                                <MenuItem value="REGULAR">Regular</MenuItem>
                                <MenuItem value="OFF_CYCLE">Off-Cycle</MenuItem>
                            </TextField>
                        </Grid>
                        {filters.run_type === 'REGULAR' && (
                            <>
                                <Grid item xs={12} sm={2}>
                                    <TextField select fullWidth label="Payroll Group" size="small" value={filters.payroll_group_id}
                                        onChange={(e) => setFilters({ ...filters, payroll_group_id: e.target.value, advance_schedule_id: '' })}>
                                        {metadata.payrollGroups.map(group => <MenuItem key={group.id} value={group.id}>{group.group_name}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField select fullWidth label="Period Type" size="small" value={filters.period_type}
                                        onChange={(e) => setFilters({ ...filters, period_type: e.target.value, advance_schedule_id: '' })}>
                                        <MenuItem value="REGULAR">Regular / Month End</MenuItem>
                                        <MenuItem value="ADVANCE">Advance</MenuItem>
                                    </TextField>
                                </Grid>
                                {filters.period_type === 'ADVANCE' && (
                                    <Grid item xs={12} sm={2}>
                                        <TextField select fullWidth label="Advance Schedule" size="small" value={filters.advance_schedule_id}
                                            onChange={(e) => setFilters({ ...filters, advance_schedule_id: e.target.value })}>
                                            {(metadata.payrollGroups.find(group => Number(group.id) === Number(filters.payroll_group_id))?.schedules || [])
                                                .filter(schedule => Number(schedule.is_active) === 1)
                                                .map(schedule => <MenuItem key={schedule.id} value={schedule.id}>{schedule.schedule_name}</MenuItem>)}
                                        </TextField>
                                    </Grid>
                                )}
                            </>
                        )}
                        {filters.run_type === 'OFF_CYCLE' && (
                            <Grid item xs={12} sm={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="off-cycle-elements-label">Off-Cycle Elements</InputLabel>
                                    <Select
                                        labelId="off-cycle-elements-label"
                                        multiple
                                        value={filters.off_cycle_elements}
                                        onChange={(e) => setFilters({ ...filters, off_cycle_elements: e.target.value })}
                                        input={<OutlinedInput label="Off-Cycle Elements" />}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.map((value) => {
                                                    const el = elements.find(e => e.id === value);
                                                    return <Chip key={value} label={el ? el.element_name : value} size="small" />;
                                                })}
                                            </Box>
                                        )}
                                    >
                                        {elements.map((el) => (
                                            <MenuItem key={el.id} value={el.id}>
                                                <Checkbox checked={filters.off_cycle_elements.indexOf(el.id) > -1} />
                                                <ListItemText primary={el.element_name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                        <Grid item>
                            <Button
                                variant="contained"
                                startIcon={<RunIcon />}
                                onClick={fetchPreview}
                                disabled={loading || (filters.run_type === 'REGULAR' && (!filters.payroll_group_id || (filters.period_type === 'ADVANCE' && !filters.advance_schedule_id)))}
                                sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#1a237e' }}
                            >
                                Calculate Preview
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="outlined"
                                startIcon={<UploadIcon />}
                                onClick={() => navigate('/payroll/adhoc')}
                                disabled={loading}
                                sx={{ borderRadius: 2, textTransform: 'none' }}
                            >
                                Ad-Hoc Variables
                            </Button>
                        </Grid>
                        {rowData.length > 0 && (
                            <Grid item>
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={onBtnExport}
                                    disabled={loading}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        color: '#10b981',
                                        borderColor: '#10b981',
                                        '&:hover': { borderColor: '#059669', bgcolor: '#f0fdf4' }
                                    }}
                                >
                                    Export to Excel
                                </Button>
                            </Grid>
                        )}

                        <Grid item sx={{ ml: 'auto' }}>
                            <Button
                                variant="outlined"
                                startIcon={<SaveIcon />}
                                onClick={createRun}
                                disabled={loading || rowData.length === 0 || (filters.run_type === 'REGULAR' && (!filters.payroll_group_id || (filters.period_type === 'ADVANCE' && !filters.advance_schedule_id) || !!existingRun || readiness?.period?.status !== 'LOCKED'))}
                                color="success"
                                sx={{ borderRadius: 2, textTransform: 'none' }}
                            >
                                Save Draft Run
                            </Button>
                        </Grid>
                    </Grid>
                </Card>

                {filters.run_type === 'REGULAR' && readiness && (
                    <Card sx={{ p: 2, borderRadius: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography fontWeight={800}>Payroll Readiness — {readiness.startDate} to {readiness.endDate}</Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip label={readiness.period?.status || 'OPEN'} color={readiness.period?.status === 'LOCKED' ? 'success' : 'default'} size="small" />
                                {['LOCKED', 'PAYROLL_DRAFTED'].includes(readiness.period?.status) ?
                                    <Button size="small" color="warning" onClick={() => changePeriodLock(false)}>Reopen Period</Button> :
                                    <Button size="small" variant="contained" disabled={!readiness.canProceed} onClick={() => changePeriodLock(true)}>Lock Period</Button>}
                            </Stack>
                        </Stack>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {readiness.checks.map(check => (
                                <Chip
                                    key={check.code}
                                    label={check.message}
                                    color={check.severity === 'PASS' ? 'success' : check.severity === 'WARNING' ? 'warning' : 'error'}
                                    variant="outlined"
                                    size="small"
                                    onClick={check.details && check.details.length > 0 ? () => setDetailsDialog({ open: true, title: check.message, details: check.details }) : undefined}
                                    sx={{ cursor: check.details && check.details.length > 0 ? 'pointer' : 'default' }}
                                />
                            ))}
                        </Stack>
                    </Card>
                )}

                {filters.run_type === 'REGULAR' && existingRun && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2, borderRadius: 2 }}
                        action={<Button color="inherit" size="small" onClick={() => navigate('/payroll/draft-review')}>View Existing Run</Button>}
                    >
                        A regular payroll run already exists for {String(filters.month).padStart(2, '0')}/{filters.year}: Run #{existingRun.id} — {existingRun.status}. Saving another draft is disabled.
                    </Alert>
                )}

                {rowData.length > 0 && (
                    <Card sx={{ p: 2, borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a237e' }}>View Filters (Does not affect payroll processing scope)</Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                                <span style={{ color: '#546e7a' }}>Total Staff: </span>
                                <span style={{ color: '#0c9c33' }}>{filteredRowData.length}</span>
                                <span style={{ color: '#cfd8dc', margin: '0 12px' }}>|</span>
                                <span style={{ color: '#546e7a' }}>Total Amount: </span>
                                <span style={{ color: '#0c9c33' }}>{filteredRowData.reduce((sum, row) => sum + (Number(row.net_pay) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </Typography>
                        </Box>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth label="Search Name / ID" size="small"
                                    value={viewFilters.searchText}
                                    onChange={(e) => setViewFilters({ ...viewFilters, searchText: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    select fullWidth label="Branch" size="small"
                                    value={viewFilters.branchId}
                                    onChange={(e) => setViewFilters({ ...viewFilters, branchId: e.target.value })}
                                >
                                    <MenuItem value="">All Branches</MenuItem>
                                    {metadata.branches.map(b => <MenuItem key={b.id} value={b.id}>{b.branch_name}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    select fullWidth label="Org Node" size="small"
                                    value={viewFilters.orgNodeId}
                                    onChange={(e) => setViewFilters({ ...viewFilters, orgNodeId: e.target.value })}
                                >
                                    <MenuItem value="">All Nodes</MenuItem>
                                    {metadata.nodes.map(n => <MenuItem key={n.id} value={n.id}>{n.node_name}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    select fullWidth label="Grade" size="small"
                                    value={viewFilters.gradeId}
                                    onChange={(e) => setViewFilters({ ...viewFilters, gradeId: e.target.value })}
                                >
                                    <MenuItem value="">All Grades</MenuItem>
                                    {metadata.grades.map(g => <MenuItem key={g.id} value={g.id}>{g.grade_name}</MenuItem>)}
                                </TextField>
                            </Grid>
                        </Grid>
                    </Card>
                )}

                {loading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

                <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)', flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Box
                        className="ag-theme-quartz"
                        sx={{ width: '100%', flexGrow: 1, height: 'calc(100vh - 340px)', minHeight: 400 }}
                    >
                        <AgGridReact
                            ref={gridRef}
                            rowData={filteredRowData}
                            columnDefs={columnDefs}
                            defaultColDef={{ sortable: true, filter: true, resizable: true }}
                            pagination={true}
                            paginationPageSize={20}
                            paginationPageSizeSelector={[10, 20, 50, 100]}
                            animateRows={true}
                            rowHeight={44}
                            headerHeight={44}
                        />
                    </Box>
                </Card>
            </Box>

            <Snackbar
                open={statusMsg.open}
                autoHideDuration={6000}
                onClose={() => setStatusMsg({ ...statusMsg, open: false })}
            >
                <Alert severity={statusMsg.severity} sx={{ width: '100%' }}>
                    {statusMsg.msg}
                </Alert>
            </Snackbar>

            <Dialog open={detailsDialog.open} onClose={() => setDetailsDialog({ ...detailsDialog, open: false })} maxWidth="sm" fullWidth>
                <DialogTitle>{detailsDialog.title}</DialogTitle>
                <DialogContent dividers>
                    <List dense>
                        {detailsDialog.details.map((staff, idx) => (
                            <ListItem key={staff.id || idx}>
                                <ListItemText primary={staff.full_name} secondary={`Staff ID: ${staff.staff_code || staff.id}`} />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialog({ ...detailsDialog, open: false })}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!breakdownPayroll} onClose={() => setBreakdownPayroll(null)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Payroll Calculation — {breakdownPayroll?.full_name || breakdownPayroll?.employee_number || ''}
                </DialogTitle>
                <DialogContent dividers>
                    <EffectiveDatedPayrollBreakdown payroll={breakdownPayroll} />
                </DialogContent>
                <DialogActions><Button onClick={() => setBreakdownPayroll(null)}>Close</Button></DialogActions>
            </Dialog>

        </Box>
    );
};

export default PayrollProcessing;
